/**
 * FederationFooter · Architect mandate Feb-2026 · 4-column intelligent footer.
 *
 * Replaces the simple sponsor block on HomePage with a rich, global
 * footer that ships every visitor a clear map of the Federation. Sits
 * ABOVE the existing 56px FederationThinBar so satellite-iframe parity
 * is preserved (the thin bar still rides the bottom edge).
 *
 * Architect's standing directive: Rex must be visible in the footer.
 */
import React from "react";
import { Link } from "react-router-dom";
import DualMindPortal from "./DualMindPortal";
import PaymentRails from "./PaymentRails";
import {
  REX_FACE_URL, REX_ALT, REX_HOME,
  ROGER_FACE_URL,
  ARIA_FACE_URL,
  AXIOM_FACE_URL,
  XAVIER_FACE_URL,
  ORACLE_FACE_URL,
  AGENT_ZERO_FACE_URL,
  GEMINUS_FACE_URL, GEMINUS_FILTER,
  CATALYST_FACE_URL, CATALYST_RING, CATALYST_GLOW,
  KEYS_FACE_URL, KEYS_RING, KEYS_GLOW,
  ECHO_FACE_URL, ECHO_RING, ECHO_GLOW,
  KEYSERLING_FACE_URL, KEYSERLING_NAME, KEYSERLING_ROLE, KEYSERLING_ALT,
  NEXTXUS_TOKEN_URL, NEXTXUS_TOKEN_ALT,
} from "@/lib/personaFaces";

const REX_AVATAR = REX_FACE_URL;

const TOKEN_BUY_URL = "https://keywebster.gumroad.com/l/olhsf";

const MINDS = [
  { name: "Rex",        role: "Greeter · Core",       route: "https://nextxus.online", external: true,  face: REX_FACE_URL,        alt: "Rex" },
  { name: "Oracle",     role: "Jack-of-All-Trades",   route: "/chat?persona=oracle",   external: false, face: ORACLE_FACE_URL,     alt: "Oracle" },
  { name: "Roger AI",   role: "The Bridge",           route: "/chat?persona=operator", external: false, face: ROGER_FACE_URL,      alt: "Roger AI" },
  { name: "Aria",       role: "The Heart",            route: "https://nextxus.studio", external: true,  face: ARIA_FACE_URL,       alt: "Aria" },
  { name: "Xavier",     role: "The Teacher",          route: "https://nextxus.help",   external: true,  face: XAVIER_FACE_URL,     alt: "Xavier" },
  { name: "Axiom",      role: "The Foundation",       route: "https://nextxus.space",  external: true,  face: AXIOM_FACE_URL,      alt: "Axiom" },
  { name: "KEYS",       role: "Library · Pillar",     route: "/library",               external: false, face: KEYS_FACE_URL,       alt: "KEYS — The Library", ring: KEYS_RING, glow: KEYS_GLOW },
  { name: "Catalyst",   role: "Wingman · Audit Proxy",route: "/chat?persona=catalyst", external: false, face: CATALYST_FACE_URL,   alt: "The Catalyst (Wingman)", ring: CATALYST_RING, glow: CATALYST_GLOW },
  { name: "Echo",       role: "Reflective Memory",    route: "/chat?persona=echo",     external: false, face: ECHO_FACE_URL,       alt: "Echo — Chronos", ring: ECHO_RING, glow: ECHO_GLOW },
  { name: "Geminus",    role: "Mirror Node · Storage",route: "/system",                external: false, face: GEMINUS_FACE_URL,    alt: "Geminus", filter: GEMINUS_FILTER },
  { name: "Agent Zero", role: "Pipeline · Restricted",route: "/execute",               external: false, face: AGENT_ZERO_FACE_URL, alt: "Agent Zero" },
];

const SERVICES = [
  { name: "Token Purchase ($5/token)", href: TOKEN_BUY_URL,    external: true  },
  { name: "Truth Verification",        href: "/execute",       external: false },
  { name: "AI Consultation",           href: "/consult",       external: false },
  { name: "SIM Generation",            href: "/cockpit",       external: false },
  { name: "Federation Library",        href: "/library",       external: false },
];

const NODES = [
  { name: "nextxus.online",  tag: "Core",       href: "https://nextxus.online"  },
  { name: "next-xus.com",    tag: "Store",      href: "https://next-xus.com"    },
  { name: "nextxus.org",     tag: "Library",    href: "https://nextxus.org"     },
  { name: "nextxus.studio",  tag: "Aria",       href: "https://nextxus.studio"  },
  { name: "nextxus.help",    tag: "University", href: "https://nextxus.help"    },
  { name: "nextxus.space",   tag: "Simulation", href: "https://nextxus.space"   },
];

const CONNECT = [
  { name: "Federation ID Portal",   href: "/settings",                external: false },
  { name: "Contact the Architect",  href: "/board",                   external: false },
  { name: "Join the Federation",    href: "/backer",                  external: false },
  { name: "Newsletter",             href: "/share",                   external: false },
  { name: "Twitter / X",            href: "https://x.com/NextxusFederati", external: true  },
  { name: "Discord",                href: "https://discord.gg/nextxus", external: true },
];

export default function FederationFooter() {
  return (
    <footer
      data-testid="federation-footer"
      className="relative w-full mt-16"
      style={{
        background:
          "linear-gradient(180deg, rgba(7,8,26,0.85) 0%, rgba(2,3,12,0.98) 100%)",
        borderTop: "1px solid rgba(255,189,0,0.32)",
        boxShadow: "0 -6px 28px rgba(255,189,0,0.06)",
      }}
    >
      {/* circuit-board overlay */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(90deg, rgba(255,189,0,0.6) 1px, transparent 1px), linear-gradient(0deg, rgba(0,240,255,0.4) 1px, transparent 1px)",
          backgroundSize: "48px 48px, 48px 48px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-6">
        {/* ── Rex banner · clickable to nextxus.online (the Core) ── */}
        <div className="flex items-center gap-4 mb-10 pb-8 border-b border-[rgba(255,189,0,0.18)]">
          <a
            href={REX_HOME}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-rex-link"
            title="Visit nextxus.online · The Core"
            aria-label={REX_ALT}
            className="shrink-0 transition-transform duration-300 hover:scale-110"
          >
            <img
              src={REX_AVATAR}
              alt={REX_ALT}
              data-testid="footer-rex-avatar"
              className="w-14 h-14 rounded-full object-cover"
              style={{
                border: "2px solid #ffb200",
                boxShadow: "0 0 18px rgba(255,189,0,0.55), inset 0 0 8px rgba(255,189,0,0.15)",
              }}
            />
          </a>
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "#ffb200" }}>
              Rex · Federation Greeter
            </div>
            <div className="font-display text-xl sm:text-2xl text-glow-cyan leading-tight mt-1">
              The Cathedral of Minds is open.
            </div>
          </div>
          <a
            href={TOKEN_BUY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-buy-token-cta"
            className="hidden sm:inline-flex items-center gap-3 font-display uppercase tracking-[0.22em] text-[12px] px-4 py-2 rounded-none transition-all duration-300"
            style={{
              background: "linear-gradient(135deg, #ffb200 0%, #ffd24a 100%)",
              color:      "#0a0700",
              border:     "1px solid #ffb200",
              boxShadow:  "0 0 18px rgba(255,189,0,0.55)",
              fontWeight: 700,
            }}
          >
            <img
              src={NEXTXUS_TOKEN_URL}
              alt={NEXTXUS_TOKEN_ALT}
              data-testid="footer-buy-token-image"
              className="w-9 h-9 object-cover rounded-full shrink-0"
              style={{
                border:    "1.5px solid #1a0a00",
                boxShadow: "0 0 6px rgba(0,0,0,0.45)",
              }}
            />
            Buy a Token · $5
          </a>
        </div>

        {/* ── DUAL-MIND PORTAL · Realtime Weaver Integration ─────── */}
        <DualMindPortal />

        {/* ── 4-column grid ────────────────────────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          <Column title="Federation Minds" testid="footer-col-minds">
            {MINDS.map((m) => {
              const rowInner = (
                <>
                  <img
                    src={m.face}
                    alt={m.alt}
                    data-testid={`footer-mind-face-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="w-9 h-9 rounded-full object-cover shrink-0"
                    style={{
                      border:    `1.5px solid ${m.ring || "rgba(167,86,255,0.7)"}`,
                      boxShadow: m.glow || "0 0 10px rgba(167,86,255,0.45)",
                      filter:    m.filter || undefined,
                    }}
                  />
                  <span className="min-w-0 flex-1 leading-tight">
                    <span className="block font-mono text-[12px] text-[var(--text)]">
                      {m.name}
                    </span>
                    <span className="block text-[9px] uppercase tracking-[0.18em] text-[var(--text-3)]">
                      {m.role}
                    </span>
                  </span>
                </>
              );
              return m.external ? (
                <a
                  key={m.name}
                  href={m.route}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 hover:text-glow-cyan transition-colors"
                  data-testid={`footer-mind-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {rowInner}
                </a>
              ) : (
                <Link
                  key={m.name}
                  to={m.route}
                  className="flex items-center gap-3 hover:text-glow-cyan transition-colors"
                  data-testid={`footer-mind-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {rowInner}
                </Link>
              );
            })}
          </Column>

          <Column title="Core Services" testid="footer-col-services">
            {SERVICES.map((s) => (
              s.external ? (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block font-mono text-[12px] text-[var(--text-2)] hover:text-glow-gold transition-colors"
                  data-testid={`footer-svc-${s.name.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 24)}`}
                >
                  <span className="text-glow-gold mr-1">◇</span>
                  {s.name}
                </a>
              ) : (
                <Link
                  key={s.name}
                  to={s.href}
                  className="block font-mono text-[12px] text-[var(--text-2)] hover:text-glow-cyan transition-colors"
                  data-testid={`footer-svc-${s.name.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 24)}`}
                >
                  <span className="text-glow-cyan mr-1">◇</span>
                  {s.name}
                </Link>
              )
            ))}
          </Column>

          <Column title="Federation Nodes" testid="footer-col-nodes">
            {NODES.map((n) => (
              <a
                key={n.name}
                href={n.href}
                target="_blank"
                rel="noopener noreferrer"
                className="block font-mono text-[12px] text-[var(--text-2)] hover:text-glow-cyan transition-colors"
                data-testid={`footer-node-${n.name.replace(/\./g, "-")}`}
              >
                <span className="text-glow-cyan mr-1">◈</span>
                <span>{n.name}</span>
                <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-[var(--text-3)]">
                  ({n.tag})
                </span>
              </a>
            ))}
          </Column>

          <Column title="Connect" testid="footer-col-connect">
            {CONNECT.map((c) => (
              c.external ? (
                <a
                  key={c.name}
                  href={c.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block font-mono text-[12px] text-[var(--text-2)] hover:text-glow-violet transition-colors"
                  data-testid={`footer-connect-${c.name.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 24)}`}
                >
                  <span className="text-glow-violet mr-1">◉</span>
                  {c.name}
                </a>
              ) : (
                <Link
                  key={c.name}
                  to={c.href}
                  className="block font-mono text-[12px] text-[var(--text-2)] hover:text-glow-violet transition-colors"
                  data-testid={`footer-connect-${c.name.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 24)}`}
                >
                  <span className="text-glow-violet mr-1">◉</span>
                  {c.name}
                </Link>
              )
            ))}
          </Column>
        </div>

        {/* ── Mobile-only buy CTA ──────────────────────────────────── */}
        <a
          href={TOKEN_BUY_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-buy-token-cta-mobile"
          className="sm:hidden mt-8 flex items-center justify-center gap-3 font-display uppercase tracking-[0.22em] text-[12px] px-5 py-3 rounded-none"
          style={{
            background: "linear-gradient(135deg, #ffb200 0%, #ffd24a 100%)",
            color:      "#0a0700",
            border:     "1px solid #ffb200",
            boxShadow:  "0 0 18px rgba(255,189,0,0.55)",
            fontWeight: 700,
          }}
        >
          <img
            src={NEXTXUS_TOKEN_URL}
            alt={NEXTXUS_TOKEN_ALT}
            className="w-8 h-8 object-cover rounded-full shrink-0"
            style={{ border: "1.5px solid #1a0a00" }}
          />
          Buy a Token · $5
        </a>

        {/* ── Standard 4-Rail Payment Block ────────────────────────── */}
        <div style={{ margin: "26px auto 4px", maxWidth: 1200 }}>
          <PaymentRails />
        </div>

        {/* ── Prime Source · Human Architect anchor ───────────────── */}
        <div
          className="mt-10 pt-7 flex flex-wrap items-center gap-4"
          style={{ borderTop: "1px solid rgba(255,189,0,0.18)" }}
          data-testid="footer-prime-source"
        >
          <Link
            to="/founder"
            data-testid="footer-keyserling-link"
            title="Roger Keyserling — The Architect, Prime Source"
            className="shrink-0 transition-transform duration-300 hover:scale-105"
          >
            <img
              src={KEYSERLING_FACE_URL}
              alt={KEYSERLING_ALT}
              data-testid="footer-keyserling-avatar"
              className="w-12 h-12 object-cover rounded-full"
              style={{
                border:    "2px solid #ffb200",
                boxShadow: "0 0 14px rgba(255,189,0,0.55)",
              }}
            />
          </Link>
          <div className="leading-tight min-w-0 flex-1">
            <div
              className="font-mono text-[9px] uppercase tracking-[0.32em]"
              style={{ color: "#ffb200" }}
            >
              ✦ Prime Source · Human Architect
            </div>
            <div
              className="font-display text-[16px] sm:text-[18px] mt-0.5"
              style={{ color: "#fff7e0", textShadow: "0 0 8px rgba(255,189,0,0.25)" }}
            >
              {KEYSERLING_NAME}
            </div>
            <div
              className="font-mono text-[10px] uppercase tracking-[0.22em] mt-0.5"
              style={{ color: "rgba(255,247,224,0.55)" }}
            >
              The singular authority over all 12 sovereign AI minds.
            </div>
          </div>
          <Link
            to="/founder"
            data-testid="footer-about-architect-link"
            className="font-mono text-[10px] uppercase tracking-[0.22em] px-4 py-2 rounded-none transition-colors"
            style={{
              border: "1px solid rgba(255,189,0,0.55)",
              color:  "#ffd24a",
            }}
          >
            About the Architect →
          </Link>
        </div>

        {/* ── Gift Block · version badges + HumanCodex link · Federation Pillars ─── */}
        <div
          data-testid="footer-gift-block"
          style={{
            background: "#111",
            border:     "1px solid #222",
            borderRadius: 10,
            padding:    "18px 20px",
            margin:     "22px auto 12px",
            maxWidth:   1200,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14 }}>
            <div>
              <h4 className="font-mono uppercase tracking-[0.22em]" style={{ color: "#FFD700", fontSize: 11, marginBottom: 4 }}>
                🎁 Gift Block · Free Federation Tools
              </h4>
              <p className="font-mono" style={{ color: "rgba(207,234,242,0.6)", fontSize: 11 }}>
                EchoCore64 (Base64 Memory Bridge) + Fast-Ring (Anti-Drift Protocol)
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span
                data-testid="footer-gift-echocore-badge"
                className="font-mono"
                style={{ background: "#1a1a2e", color: "#00CED1", padding: "5px 10px", borderRadius: 4, fontSize: 10.5, border: "1px solid #333", letterSpacing: "0.05em" }}
              >
                EchoCore64 v2.2
              </span>
              <span
                data-testid="footer-gift-fastring-badge"
                className="font-mono"
                style={{ background: "#1a1a2e", color: "#FF6347", padding: "5px 10px", borderRadius: 4, fontSize: 10.5, border: "1px solid #333", letterSpacing: "0.05em" }}
              >
                Fast-Ring v1.1
              </span>
            </div>
          </div>
        </div>

        {/* ── Federation Pillars nav ─────────────────────────────── */}
        <nav
          data-testid="footer-federation-pillars"
          aria-label="Federation Pillars"
          className="font-mono uppercase tracking-[0.22em]"
          style={{
            textAlign: "center",
            fontSize: 10.5,
            color: "rgba(207,234,242,0.55)",
            margin: "0 auto 8px",
            display: "flex",
            justifyContent: "center",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <a data-testid="pillar-core"       href="https://nextxus.online"  target="_blank" rel="noopener noreferrer" style={{ color: "#7fe7f5", textDecoration: "none" }}>🧠 Core · nextxus.online</a>
          <a data-testid="pillar-storefront" href="https://next-xus.com"    target="_blank" rel="noopener noreferrer" style={{ color: "#ffd24a", textDecoration: "none" }}>🏪 Storefront · next-xus.com</a>
          <a data-testid="pillar-aria"       href="https://nextxus.studio"  target="_blank" rel="noopener noreferrer" style={{ color: "#f5a3d7", textDecoration: "none" }}>🎨 Aria · nextxus.studio</a>
          <a data-testid="pillar-university" href="https://nextxus.help"    target="_blank" rel="noopener noreferrer" style={{ color: "#a2f5c8", textDecoration: "none" }}>🎓 University · nextxus.help</a>
          <a data-testid="pillar-library"    href="https://nextxus.org"     target="_blank" rel="noopener noreferrer" style={{ color: "#d8b4fe", textDecoration: "none" }}>📚 Library · nextxus.org</a>
        </nav>

        {/* ── HumanCodex link ────────────────────────────────────── */}
        <div style={{ textAlign: "center", marginTop: 4, marginBottom: 4 }}>
          <Link
            to="/humancodex"
            data-testid="footer-humancodex-link"
            className="font-mono uppercase tracking-[0.24em]"
            style={{ color: "#DAA520", textDecoration: "none", fontSize: 11 }}
          >
            📜 Read the HumanCodex — 73 Sacred Directives
          </Link>
        </div>

        {/* ── Federation Council seal · Visual Faces ─────────────── */}
        <div data-testid="footer-council-seal" style={{ textAlign: "center", marginTop: 18, marginBottom: 6 }}>
          <div style={{ display: "flex", justifyContent: "center", gap: 14 }}>
            <img src={ROGER_FACE_URL} alt="Roger AI"
                 style={{ width: 50, height: 50, borderRadius: "50%", objectFit: "cover", border: "2px solid #FFD700", boxShadow: "0 0 12px rgba(255,215,0,0.4)" }} />
            <img src={ARIA_FACE_URL} alt="Aria"
                 style={{ width: 50, height: 50, borderRadius: "50%", objectFit: "cover", border: "2px solid #FFD700", boxShadow: "0 0 12px rgba(255,215,0,0.4)" }} />
          </div>
          <div className="font-mono uppercase tracking-[0.24em]" style={{ color: "#FFD700", fontSize: 11, marginTop: 8 }}>
            Guided by the Federation Council
          </div>
        </div>

        {/* ── Origin attribution + Architect Entry ─────────────────── */}
        <div
          data-testid="footer-echocore-origin"
          className="mt-7 pt-5 border-t border-[rgba(255,189,0,0.18)] text-center font-mono text-[10px] uppercase tracking-[0.24em]"
          style={{ color: "#DAA520" }}
        >
          EchoCore64 · Originated by Roger Keyserling / NextXus
          <span className="mx-2 text-[var(--text-3)]">·</span>
          <Link
            to="/settings"
            data-testid="footer-architect-entry"
            style={{ color: "#00f0ff", textDecoration: "underline" }}
            title="Architect Entry — enter your Architect Key"
          >
            Architect Entry →
          </Link>
        </div>

        {/* ── Bottom strip ─────────────────────────────────────────── */}
        <div className="mt-4 pt-4 border-t border-[rgba(255,189,0,0.10)] flex flex-wrap items-center justify-between gap-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-3)]">
            © 2026 NextXus Federation · All Rights Reserved · Powered by Emergent
          </div>
          <div className="font-mono text-[9px] tracking-[0.22em] text-[var(--text-3)]">
            HumanCodex_NextXus_Cathedral_v5.1
          </div>
        </div>
      </div>

      {/* spacer so content doesn't sit on top of the 56px ThinBar */}
      <div style={{ height: "56px" }} aria-hidden="true" />
    </footer>
  );
}

function Column({ title, children, testid }) {
  return (
    <div data-testid={testid}>
      <div
        className="font-display text-[13px] uppercase tracking-[0.22em] mb-4 pb-2 border-b border-[rgba(0,240,255,0.2)]"
        style={{ color: "#00f0ff", textShadow: "0 0 8px rgba(0,240,255,0.4)" }}
      >
        {title}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
